"use client";

import { motion } from "framer-motion";
import { Search, Lightbulb, Code, Rocket, TrendingUp, CheckCircle } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const processSteps = [
  {
    week: "Week 1",
    icon: <Search className="w-6 h-6" />,
    title: "Discovery & Analysis",
    description: "We dive deep into your workflows, identifying automation opportunities and pain points.",
    deliverables: ["Process audit", "Automation roadmap", "ROI projections"],
    color: "from-blue-500 to-blue-600"
  },
  {
    week: "Week 2-3",
    icon: <Lightbulb className="w-6 h-6" />,
    title: "Design & Architecture",
    description: "Custom AI agents are designed to seamlessly integrate with your existing systems.",
    deliverables: ["System architecture", "AI agent blueprints", "Integration plan"],
    color: "from-purple-500 to-purple-600"
  },
  {
    week: "Week 4-5",
    icon: <Code className="w-6 h-6" />,
    title: "Development & Testing",
    description: "Your AI automation system is built, tested, and refined to perfection.",
    deliverables: ["AI agents development", "Rigorous testing", "Performance optimization"],
    color: "from-green-500 to-green-600"
  },
  {
    week: "Week 6",
    icon: <Rocket className="w-6 h-6" />,
    title: "Deployment & Training",
    description: "Smooth rollout with comprehensive team training and ongoing support.",
    deliverables: ["System deployment", "Team training", "Documentation"],
    color: "from-orange-500 to-orange-600"
  },
  {
    week: "Ongoing",
    icon: <TrendingUp className="w-6 h-6" />,
    title: "Optimization & Growth",
    description: "Continuous monitoring, optimization, and scaling as your business grows.",
    deliverables: ["Performance monitoring", "Continuous optimization", "Scaling support"],
    color: "from-primary to-accent"
  }
];

const ProcessTimeline = () => {
  return (
    <section className="py-20 px-6 bg-gradient-to-br from-background via-secondary/5 to-background">
      <div className="container max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <SectionHeading title="The Transformation Journey" className="text-center" />
          <p className="text-lg text-muted-foreground mt-4 max-w-3xl mx-auto">
            From chaos to clarity in just 6 weeks. Here&apos;s exactly how we transform your business operations.
          </p>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary via-accent to-primary rounded-full" />
          
          {processSteps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true, margin: "-100px" }}
              className={`relative mb-16 lg:mb-24 ${
                index % 2 === 0 ? 'lg:pr-1/2 lg:text-right' : 'lg:pl-1/2 lg:ml-auto lg:text-left'
              }`}
            >
              {/* Timeline dot */}
              <div className="hidden lg:block absolute top-8 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-primary rounded-full border-4 border-background shadow-lg z-10" />
              
              <div className={`lg:w-5/6 ${index % 2 === 0 ? 'lg:mr-auto' : 'lg:ml-auto'}`}>
                <div className="bg-card border border-border rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className={`flex items-center gap-4 mb-6 ${index % 2 === 0 ? 'lg:justify-end' : 'lg:justify-start'}`}>
                    <div className={`bg-gradient-to-r ${step.color} p-3 rounded-full text-white`}>
                      {step.icon}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-primary">{step.week}</div>
                      <h3 className="text-2xl font-bold">{step.title}</h3>
                    </div>
                  </div>
                  
                  <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                    {step.description}
                  </p>
                  
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm uppercase tracking-wide text-primary">Key Deliverables:</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {step.deliverables.map((deliverable, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{deliverable}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              The End Result: A Business That Runs Itself
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              By week 6, you&apos;ll have a fully automated system that handles your routine tasks, 
              freeing your team to focus on growth, innovation, and what they do best. 
              Most clients see ROI within the first month of deployment.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">80%</div>
                <div className="text-sm text-muted-foreground">Time Saved</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">99.9%</div>
                <div className="text-sm text-muted-foreground">Accuracy Rate</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">3 Months</div>
                <div className="text-sm text-muted-foreground">Payback Period</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessTimeline; 